/**
 * The small parts the three tables of the Similar watersheds tab share (client, 7 October
 * 2026: "proper arrangement and alignment and design, less cluttered, clear").
 *
 * `#uc2Scaling`, `#uc2Similar` and `#uc2Contrasts` are three tables on one column rhythm:
 * the name column is 34 % of the card, the bar column 26 %, the rest share what is left.
 * Every bar is the same slim SVG track, every number is set in the mono face, and every
 * "how alike" reading is the same bar with its percentage at the end, so a reader who has
 * learned one table has learned the other two.
 */
import type { ReactNode } from "react";

export interface MiniBarProps {
  /** 0 to 1; `null` draws the empty track. */
  value: number | null;
  color?: string;
  label: string;
  testId?: string;
}

/**
 * One slim horizontal bar on a 0 to 1 scale, drawn in SVG. It stretches to its cell (the
 * view box is not kept in proportion, which is harmless for two rectangles) and carries no
 * text: the number beside it is HTML, so it is set in the mono face at the cell's size.
 */
export function MiniBar({ value, color, label, testId }: MiniBarProps) {
  const w = value == null ? 0 : Math.min(1, Math.max(0, value)) * 100;
  return (
    <svg
      className="uc2-mbar"
      viewBox="0 0 100 6"
      preserveAspectRatio="none"
      role="img"
      aria-label={label}
      data-testid={testId}
      focusable="false"
    >
      <rect
        className="uc2-mbar-track"
        x="0"
        y="0"
        width="100"
        height="6"
        rx="3"
      />
      {w > 0 ? (
        <rect
          className="uc2-mbar-fill"
          x="0"
          y="0"
          width={w}
          height="6"
          rx="3"
          style={color ? { fill: color } : undefined}
        />
      ) : null}
    </svg>
  );
}

/** A bar with its reading at the end: the "alike" cell, the score cell, a paired side. */
export function BarReading({
  value,
  text,
  color,
  label,
  testId,
}: {
  value: number | null;
  text: ReactNode;
  color?: string;
  label: string;
  testId?: string;
}) {
  return (
    <span className="uc2-bar" data-testid={testId}>
      <MiniBar value={value} color={color} label={label} />
      <span className="uc2-num">{text}</span>
    </span>
  );
}
